/**
 * @quotech/ui - Email Accounts Settings Tab
 * 
 * PATH: packages/ui/src/components/settings/EmailAccountsTab.tsx
 * PURPOSE: Orchestrator for connected email accounts (Outlook, Gmail) with OAuth
 *          flows and shared access control.
 * WHY: Admin-only tab under Integrations category.
 *
 * FLOW:
 *   ┌────────────┐     ┌──────────────────┐     ┌──────────────────┐
 *   │ API Client │  →  │ EmailAccountsTab │  →  │ EmailAccountCard │
 *   └────────────┘     └──────────────────┘     └──────────────────┘
 *
 * DEPENDENCIES:
 *   - ./email-accounts-tab/email-accounts-tab-types: types, config, helpers
 *   - ./email-accounts-tab/EmailAccountCard: individual account card
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Mail,
  AlertCircle,
  Loader2,
  ExternalLink,
  CheckCircle2,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { PROVIDER_CONFIG } from './email-accounts-tab/email-accounts-tab-types'
import { EmailAccountCard } from './email-accounts-tab/EmailAccountCard'
import { OutlookAdminConsentBanner } from './OutlookAdminConsentBanner'
import type {
  AccessLevel,
  EmailAccountData,
  EmailAccountsTabProps,
  OrgMemberOption,
} from './email-accounts-tab/email-accounts-tab-types'

// Re-export types so existing consumers don't break
export type { EmailAccountData, EmailAccountsTabProps }

function parseOutlookCallbackParams(): {
  connected: boolean;
  email: string;
  adminConsent: { required: boolean; url: string } | null;
  errorMessage: string;
} {
  if (typeof window === 'undefined') return { connected: false, email: '', adminConsent: null, errorMessage: '' }
  const params = new URLSearchParams(window.location.search)
  const outlookConnected = params.get('outlook_connected')
  const outlookError = params.get('outlook_error')
  const message = params.get('message') || ''
  const adminConsentUrl = params.get('admin_consent_url') || ''
  const email = params.get('email') || ''

  if (outlookConnected === 'true' || outlookConnected === '1') {
    return { connected: true, email: decodeURIComponent(email), adminConsent: null, errorMessage: '' }
  }
  if (outlookError === 'true' || outlookError === '1') {
    const decodedMsg = decodeURIComponent(message.replace(/\+/g, ' '))
    const isConsentError = decodedMsg === 'admin_consent_required'
      || decodedMsg.includes('AADSTS65001')
      || decodedMsg.includes('consent')

    if (isConsentError) {
      return {
        connected: false,
        email: '',
        adminConsent: {
          required: true,
          url: adminConsentUrl ? decodeURIComponent(adminConsentUrl) : '',
        },
        errorMessage: '',
      }
    }
    return { connected: false, email: '', adminConsent: null, errorMessage: decodedMsg }
  }
  return { connected: false, email: '', adminConsent: null, errorMessage: '' }
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function EmailAccountsTab({
  apiClient,
  organisationId,
  isAdmin = false,
  currentUserId,
  className,
}: EmailAccountsTabProps) {
  const [accounts, setAccounts] = useState<EmailAccountData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null)
  const [outlookSuccess, setOutlookSuccess] = useState<string | null>(null)
  const [adminConsent, setAdminConsent] = useState<{ required: boolean; url: string } | null>(null)
  const [members, setMembers] = useState<OrgMemberOption[]>([])

  // Parse callback URL params on mount
  useEffect(() => {
    const result = parseOutlookCallbackParams()
    if (result.connected) {
      setOutlookSuccess(result.email || 'Connected')
    } else if (result.adminConsent?.required) {
      setAdminConsent(result.adminConsent)
    } else if (result.errorMessage) {
      setError(result.errorMessage)
    }
  }, [])

  // Fetch accounts on mount
  useEffect(() => {
    if (!apiClient) return
    
    setIsLoading(true)
    apiClient.get('/email-accounts/')
      .then((res: any) => {
        // @quotech/api's client resolves the FULL axios response (callers
        // elsewhere do `const { data } = await apiClient.get(...)`), whereas
        // the email-hub fetch wrapper resolves the JSON body directly.
        // Tolerate BOTH shapes — reading `res.accounts` on an axios response
        // was silently empty, which is why a freshly-added mailbox never
        // showed here ("I added another mailbox and it didn't show").
        const body = res?.data ?? res
        setAccounts(body?.accounts ?? (Array.isArray(body) ? body : []))
        setError(null)
      })
      .catch((err: any) => {
        console.warn('[EmailAccountsTab] Failed to load accounts:', err)
        setError('Failed to load email accounts')
      })
      .finally(() => setIsLoading(false))
  }, [apiClient])

  // Org members for the "share mailbox with" picker. Optional — if the host
  // didn't pass organisationId (or the call fails) the picker just stays
  // empty and removal still works. Tolerates both axios and fetch shapes.
  useEffect(() => {
    if (!apiClient || !organisationId) return
    let cancelled = false
    apiClient.get(`/organisations/${organisationId}/members/`)
      .then((res: any) => {
        if (cancelled) return
        const body = res?.data ?? res
        const raw: any[] = body?.members ?? (Array.isArray(body) ? body : [])
        setMembers(
          raw
            .map((m) => ({
              id: m.id,
              name:
                m.full_name ||
                `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() ||
                m.email ||
                '',
              email: m.email || '',
            }))
            .filter((m) => m.id != null && (m.name || m.email)),
        )
      })
      .catch(() => {
        /* members are optional; the add-person picker stays empty */
      })
    return () => {
      cancelled = true
    }
  }, [apiClient, organisationId])

  const handleConnect = useCallback(async (provider: 'outlook' | 'gmail') => {
    if (!apiClient) return
    setConnectingProvider(provider)
    try {
      const config = PROVIDER_CONFIG[provider]
      const res: any = await apiClient.get(config.connectUrl)
      // Same response-shape tolerance as the accounts fetch above — the
      // authorization_url sits at res.data with the @quotech/api client.
      const body = res?.data ?? res
      if (body?.authorization_url) {
        // Display-mode aware (operator 2026-07-08 "redirected me back to
        // chrome instead of the app"): navigating a standalone PWA window
        // to the provider takes the whole session out of app scope and the
        // OAuth callback lands in a browser tab, never returning to the
        // installed app. From a PWA, open the provider in a separate
        // context so this window survives; the accounts list refreshes on
        // return. Inline copy of tenant-shared/lib/oauthNavigation.ts —
        // @quotech/ui must not depend on tenant-shared (boundary).
        let standalone = false
        try {
          standalone =
            window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
            (navigator as Navigator & { standalone?: boolean }).standalone === true
        } catch { /* default to same-window */ }
        const opened = standalone
          ? window.open(body.authorization_url, '_blank', 'noopener,noreferrer')
          : null
        if (!opened) window.location.href = body.authorization_url
      }
    } catch (err) {
      console.error(`Failed to start ${provider} OAuth:`, err)
      setError(`Failed to connect ${provider}`)
    } finally {
      setConnectingProvider(null)
    }
  }, [apiClient])

  const handleToggleSync = useCallback(async (accountId: number, enabled: boolean) => {
    if (!apiClient) return
    try {
      await apiClient.patch?.(`/email-accounts/${accountId}/`, { sync_enabled: enabled })
      setAccounts(prev => prev.map(a => 
        a.id === accountId ? { ...a, sync_enabled: enabled } : a
      ))
    } catch (err) {
      console.error('Failed to toggle sync:', err)
    }
  }, [apiClient])

  // Privacy toggle — owner-only (backend enforces 403 for non-owners).
  // 'personal' = private to the owner; 'shared' = visible to org members
  // with an access grant. Optimistically reflect the new type on success.
  const handleSetPrivacy = useCallback(async (accountId: number, makePrivate: boolean) => {
    if (!apiClient?.patch) return
    const next: 'personal' | 'shared' = makePrivate ? 'personal' : 'shared'
    try {
      await apiClient.patch(`/email-accounts/${accountId}/`, { account_type: next })
      setAccounts(prev => prev.map(a =>
        a.id === accountId ? { ...a, account_type: next } : a
      ))
      setError(null)
    } catch (err) {
      console.error('Failed to update mailbox privacy:', err)
      setError('Could not update mailbox privacy. Only the mailbox owner can change this.')
    }
  }, [apiClient])

  // Kanban approval toggle — owner OR org admin (backend enforces 403 + the
  // private-locked-on rule). When ON, emails from this mailbox wait for manual
  // approval before the auto-classifier files them onto a board. Reflect the
  // authoritative server response (echoes require_kanban_approval +
  // kanban_approval_locked) so a private mailbox flips to "locked on".
  const handleSetApproval = useCallback(async (accountId: number, requireApproval: boolean) => {
    if (!apiClient?.patch) return
    try {
      const res: any = await apiClient.patch(
        `/email-accounts/${accountId}/`,
        { require_kanban_approval: requireApproval },
      )
      const body = res?.data ?? res
      setAccounts(prev => prev.map(a =>
        a.id === accountId
          ? {
              ...a,
              require_kanban_approval:
                body?.require_kanban_approval ?? requireApproval,
              kanban_approval_locked:
                body?.kanban_approval_locked ?? a.kanban_approval_locked,
            }
          : a
      ))
      setError(null)
    } catch (err) {
      console.error('Failed to update mailbox approval gate:', err)
      setError(
        'Could not update the approval setting. A private mailbox locks ' +
        'approval on - switch it to shared first to disable.'
      )
    }
  }, [apiClient])

  // Apply the access_grants the endpoint echoes back so the card reflects
  // the authoritative server state (handles reactivation / level changes).
  const applyGrants = useCallback((accountId: number, body: any) => {
    const grants = body?.access_grants
    if (Array.isArray(grants)) {
      setAccounts(prev => prev.map(a =>
        a.id === accountId ? { ...a, access_grants: grants } : a
      ))
      return true
    }
    return false
  }, [])

  // Share a mailbox with an org member (owner-only on the backend).
  const handleAddGrant = useCallback(async (
    accountId: number, userId: number, accessLevel: AccessLevel,
  ) => {
    if (!apiClient?.post) return
    try {
      const res: any = await apiClient.post(
        `/email-accounts/${accountId}/access/`,
        { user_id: userId, access_level: accessLevel },
      )
      applyGrants(accountId, res?.data ?? res)
      setError(null)
    } catch (err) {
      console.error('Failed to share mailbox:', err)
      setError('Could not share mailbox. Only the mailbox owner can manage sharing.')
    }
  }, [apiClient, applyGrants])

  // Revoke a member's access. Falls back to a local drop if the endpoint
  // doesn't echo the updated list.
  const handleRemoveGrant = useCallback(async (accountId: number, grantId: number) => {
    if (!apiClient?.delete) return
    try {
      const res: any = await apiClient.delete(
        `/email-accounts/${accountId}/access/${grantId}/`,
      )
      const applied = applyGrants(accountId, res?.data ?? res)
      if (!applied) {
        setAccounts(prev => prev.map(a =>
          a.id === accountId
            ? { ...a, access_grants: (a.access_grants ?? []).filter(g => g.id !== grantId) }
            : a
        ))
      }
      setError(null)
    } catch (err) {
      console.error('Failed to update mailbox sharing:', err)
      setError('Could not update mailbox sharing.')
    }
  }, [apiClient, applyGrants])

  const handleDeactivate = useCallback(async (accountId: number) => {
    if (!apiClient) return
    if (!window.confirm('Are you sure you want to disconnect this email account?')) return
    
    try {
      await apiClient.delete(`/email-accounts/${accountId}/`)
      setAccounts(prev => prev.filter(a => a.id !== accountId))
    } catch (err) {
      console.error('Failed to deactivate account:', err)
    }
  }, [apiClient])

  const handleRetryOutlook = useCallback(() => {
    setAdminConsent(null)
    handleConnect('outlook')
  }, [handleConnect])

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    )
  }

  if (!apiClient) {
    return (
      <div className={cn('text-center py-12', className)}>
        <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-3" />
        <p className="text-sm text-slate-500">API client not configured</p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-8', className)}>
      {/* Outlook success */}
      {outlookSuccess && (
        <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          Outlook connected as <span className="font-medium">{outlookSuccess}</span>
        </div>
      )}

      {/* Admin consent required */}
      {adminConsent?.required && (
        <OutlookAdminConsentBanner
          adminConsentUrl={adminConsent.url}
          onRetry={handleRetryOutlook}
          onDismiss={() => setAdminConsent(null)}
        />
      )}

      {/* Error display */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Connected accounts */}
      {accounts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
            Connected Email Accounts
          </h3>
          <div className="space-y-3">
            {accounts.map((account) => (
              <EmailAccountCard
                key={account.id}
                account={account}
                onToggleSync={handleToggleSync}
                onDeactivate={handleDeactivate}
                currentUserId={currentUserId}
                onSetPrivacy={handleSetPrivacy}
                onSetApproval={handleSetApproval}
                members={members}
                onAddGrant={handleAddGrant}
                onRemoveGrant={handleRemoveGrant}
              />
            ))}
          </div>
        </div>
      )}

      {/* Connect new account */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
          {accounts.length > 0 ? 'Connect Another Account' : 'Connect an Email Account'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Outlook */}
          <button
            onClick={() => handleConnect('outlook')}
            disabled={connectingProvider === 'outlook'}
            className={cn(
              'p-4 rounded-xl border border-slate-200 dark:border-slate-700',
              'bg-white dark:bg-slate-800 hover:border-blue-300 dark:hover:border-blue-600',
              'transition-colors text-left group',
              connectingProvider === 'outlook' && 'opacity-60 cursor-not-allowed'
            )}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Microsoft Outlook</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Microsoft 365 / Outlook.com</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 group-hover:underline">
              {connectingProvider === 'outlook' ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ExternalLink className="w-3.5 h-3.5" />
              )}
              Connect with Microsoft
            </div>
          </button>

          {/* Gmail */}
          <button
            onClick={() => handleConnect('gmail')}
            disabled={connectingProvider === 'gmail'}
            className={cn(
              'p-4 rounded-xl border border-slate-200 dark:border-slate-700',
              'bg-white dark:bg-slate-800 hover:border-red-300 dark:hover:border-red-600',
              'transition-colors text-left group',
              connectingProvider === 'gmail' && 'opacity-60 cursor-not-allowed'
            )}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Gmail</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Google Workspace / Gmail</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400 group-hover:underline">
              {connectingProvider === 'gmail' ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ExternalLink className="w-3.5 h-3.5" />
              )}
              Connect with Google
            </div>
          </button>
        </div>
      </div>

      {/* Empty state */}
      {accounts.length === 0 && !isLoading && (
        <div className="text-center py-6">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No email accounts connected yet. Connect an account above to get started.
          </p>
        </div>
      )}
    </div>
  )
}

export default EmailAccountsTab
